import { Skeleton } from "@Light/components/page/Skeleton";
import { useOptionalRate } from "@Light/utils/plan";
import { getPlanLinks } from "@Light/utils/planLinks";
import { File02 } from "@untitledui/icons";
import clsx from "clsx";
import { DocumentLink } from "./DocumentLink";

type PlanDocument = {
  href: string | undefined;
  label: string;
  document: string;
};

const PlanDocumentRow = ({ href, label, document }: PlanDocument) => {
  const icon = (
    <File02 className="size-3 text-fg-quaternary stroke-[2.5px] group-hover:text-tertiary" />
  );

  if (!href) {
    return (
      <div className="flex gap-xs items-center justify-between px-lg py-sm text-tertiary opacity-50">
        <div className="text-xs font-medium text-nowrap">{label}</div>
        {icon}
      </div>
    );
  }

  return (
    <DocumentLink href={href} document={document}>
      <div className="group flex gap-xs items-center justify-between px-lg py-sm text-tertiary hover:text-primary">
        <div className="text-xs font-medium text-nowrap">{label}</div>
        {icon}
      </div>
    </DocumentLink>
  );
};

const SKELETON_WIDTHS = ["w-4/5", "w-full", "w-11/12"];

export type PlanDocumentsProps = {
  isLoading?: boolean;
};

export const PlanDocuments = ({ isLoading }: PlanDocumentsProps) => {
  const rate = useOptionalRate();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-xs">
        <div className="text-xs px-lg text-tertiary">Plan documents</div>
        {SKELETON_WIDTHS.map((width, index) => (
          <div
            key={index}
            className="flex items-center justify-between gap-xs px-lg py-sm"
          >
            <Skeleton className={clsx("h-4 my-px", width)} />
            <Skeleton className="size-3 shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  const documents: PlanDocument[] = (rate ? getPlanLinks(rate) : []).map(
    ({ name, short_name, url }) => ({
      href: url,
      label: name,
      document: short_name,
    }),
  );

  return (
    <div className="flex flex-col gap-xs">
      <div className="text-xs px-lg text-tertiary">Plan documents</div>
      {documents.map((doc) => (
        <PlanDocumentRow key={doc.document} {...doc} />
      ))}
    </div>
  );
};
